import { useState, useRef, useCallback, useEffect } from 'react';

export const useEnhancedGamiPass = (options = {}) => {
  const {
    onOpenAuth,
    onConfirm,
    onCanceled,
    onNotFound,
    onError,
    onWindowClosed,
    windowFeatures = 'width=1024,height=1024,resizable=yes,scrollbars=yes',
    pollingInterval = 500,
    timeout = 300000, // 5 минут
    allowedOrigins = [],
    debug = false,
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [popupState, setPopupState] = useState('idle'); // idle, opening, open, closing, closed

  const popupWindow = useRef(null);
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const messageHandlers = useRef({});
  const hasHandledEvent = useRef(false); // Флаг для отслеживания обработанных событий

  // Логирование для отладки
  const log = useCallback(
    (message, data) => {
      if (debug) {
        console.log(`[useGamiPass] ${message}`, data || '');
      }
    },
    [debug],
  );

  // Проверка origin для безопасности
  const checkOrigin = useCallback(
    (origin) => {
      if (allowedOrigins.length === 0) return true;
      return allowedOrigins.includes(origin);
    },
    [allowedOrigins],
  );

  // Регистрация обработчиков сообщений
  const registerMessageHandler = useCallback((type, handler) => {
    messageHandlers.current[type] = handler;
  }, []);

  // Удаление обработчика
  const unregisterMessageHandler = useCallback((type) => {
    delete messageHandlers.current[type];
  }, []);

  // Очистка всех ресурсов
  const cleanup = useCallback(() => {
    log('Cleanup started');

    // Сбрасываем флаг обработанного события
    hasHandledEvent.current = false;

    // Очищаем интервалы и таймауты
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Удаляем обработчик сообщений
    window.removeEventListener('message', handleMessage);

    // Закрываем окно если открыто
    if (popupWindow.current && !popupWindow.current.closed) {
      try {
        popupWindow.current.close();
        log('Popup window closed');
      } catch (err) {
        log('Error closing popup window', err);
      }
    }

    // Сбрасываем состояние
    popupWindow.current = null;
    setLoading(false);
    setIsWindowOpen(false);
    setPopupState('closed');
    setError(null);

    log('Cleanup completed');
  }, [log]);

  // Сброс состояния
  const reset = useCallback(() => {
    log('Reset called');
    cleanup();
    setPopupState('idle');
    hasHandledEvent.current = false;
  }, [cleanup, log]);

  // Обработчик сообщений от popup окна (используем useCallback с зависимостями)
  const handleMessage = useCallback(
    (event) => {
      const { origin, data } = event;

      log('Message received from origin', origin);
      log('Message data', data);

      // Проверяем безопасность origin
      if (!checkOrigin(origin)) {
        log('Message from unauthorized origin blocked', origin);
        return;
      }

      const { type, ...messageData } = data || {};

      if (!type) {
        log('No message type provided');
        return;
      }

      // Проверяем, не было ли уже обработано событие для этого окна
      if (hasHandledEvent.current) {
        log('Event already handled, ignoring duplicate', type);
        return;
      }

      // Устанавливаем флаг, что событие обработано
      hasHandledEvent.current = true;

      // Выполняем зарегистрированные обработчики
      if (messageHandlers.current[type]) {
        messageHandlers.current[type](messageData);
        return;
      }

      // Стандартные обработчики
      switch (type) {
        case 'gamiPass:confirm':
          log('Confirm message received', messageData);
          cleanup();
          onConfirm?.(messageData);
          break;

        case 'gamiPass:canceled':
          log('Canceled message received', messageData);
          cleanup();
          onCanceled?.(messageData?.reason || 'unknown', messageData);
          break;

        case 'gamiPass:notFound':
          log('Not found message received');
          cleanup();
          onNotFound?.();
          break;

        case 'gamiPass:error':
          log('Error message received', messageData);
          cleanup();
          setError(messageData?.message || 'Ошибка при обработке транзакции');
          onError?.(new Error(messageData?.message || 'Ошибка при обработке транзакции'));
          break;

        case 'gamiPass:authSuccess':
          log('Auth success message received', messageData);
          // Можно передать данные об успешной авторизации
          break;

        case 'gamiPass:loaded':
          log('Popup window loaded');
          setPopupState('open');
          break;

        case 'gamiPass:closing':
          log('Popup window closing');
          setPopupState('closing');
          break;

        default:
          log('Unknown message type', type);
          // Сбрасываем флаг для неизвестных типов
          hasHandledEvent.current = false;
          break;
      }
    },
    [checkOrigin, cleanup, onConfirm, onCanceled, onNotFound, onError, log],
  );

  // Проверка состояния popup окна
  const checkPopupStatus = useCallback(() => {
    if (popupWindow.current) {
      if (popupWindow.current.closed) {
        log('Popup window is closed');

        // Проверяем, не было ли уже обработано закрытие
        if (!hasHandledEvent.current) {
          cleanup();
          onWindowClosed?.();
          onCanceled?.('popup_closed');
        }
      } else {
        setIsWindowOpen(true);
      }
    }
  }, [cleanup, onWindowClosed, onCanceled, log]);

  // Отправка сообщения в popup окно
  const sendMessageToPopup = useCallback(
    (type, data) => {
      if (popupWindow.current && !popupWindow.current.closed) {
        try {
          popupWindow.current.postMessage({ type, ...data }, '*');
          log('Message sent to popup', { type, data });
          return true;
        } catch (err) {
          log('Error sending message to popup', err);
          return false;
        }
      }
      return false;
    },
    [log],
  );

  // Основная функция для открытия URL
  const setUrl = useCallback(
    (url, customFeatures) => {
      log('setUrl called with URL', url);

      // Сбрасываем предыдущее состояние
      reset();

      try {
        // Валидация URL
        if (!url || typeof url !== 'string') {
          throw new Error('Неверный URL');
        }

        // Проверяем, что это валидный URL
        try {
          new URL(url);
        } catch (e) {
          throw new Error('Некорректный формат URL');
        }

        setPopupState('opening');
        setLoading(true);

        // Открываем новое окно
        const features = customFeatures || windowFeatures;
        popupWindow.current = window.open(url, 'GamiPassAuth', features);

        if (!popupWindow.current) {
          throw new Error('Не удалось открыть окно. Разрешите всплывающие окна в настройках браузера.');
        }

        log('Popup window opened');

        // Фокус на новом окне
        popupWindow.current.focus();

        // Удаляем старый обработчик (если есть) и добавляем новый
        window.removeEventListener('message', handleMessage);
        window.addEventListener('message', handleMessage);

        // Запускаем проверку статуса окна
        pollingIntervalRef.current = setInterval(checkPopupStatus, pollingInterval);

        // Устанавливаем таймаут
        timeoutRef.current = setTimeout(() => {
          log('Timeout reached');
          if (!hasHandledEvent.current) {
            cleanup();
            setError('Время ожидания истекло. Попробуйте снова.');
            onCanceled?.('timeout');
          }
        }, timeout);

        setIsWindowOpen(true);

        // Вызываем onOpenAuth только один раз при открытии
        if (!hasHandledEvent.current) {
          onOpenAuth?.();
        }
      } catch (err) {
        log('Error in setUrl', err);
        const errorMessage = err.message || 'Неизвестная ошибка';
        setError(errorMessage);
        if (!hasHandledEvent.current) {
          onError?.(new Error(errorMessage));
        }
        cleanup();
      }
    },
    [reset, windowFeatures, handleMessage, checkPopupStatus, pollingInterval, timeout, cleanup, onOpenAuth, onCanceled, onError, log],
  );

  // Ручное закрытие окна
  const closeWindow = useCallback(() => {
    log('Manual close called');
    if (!hasHandledEvent.current) {
      cleanup();
      onCanceled?.('manual_close');
    }
  }, [cleanup, onCanceled, log]);

  // Проверка, готово ли окно к приему сообщений
  const isPopupReady = useCallback(() => {
    return popupWindow.current && !popupWindow.current.closed;
  }, []);

  // Получение информации о popup окне
  const getPopupInfo = useCallback(() => {
    if (!popupWindow.current) return null;

    return {
      closed: popupWindow.current.closed,
      location: popupWindow.current.location?.href || 'unknown',
      readyState: popupWindow.current.document?.readyState || 'unknown',
    };
  }, []);

  // Автоматическая очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      log('Component unmounting, cleaning up');
      cleanup();
    };
  }, [cleanup, log]);

  // Предзарегистрируем стандартные обработчики
  useEffect(() => {
    // Пример кастомного обработчика
    registerMessageHandler('gamiPass:custom', (data) => {
      log('Custom handler called', data);
    });

    // Очистка при размонтировании
    return () => {
      messageHandlers.current = {};
    };
  }, [registerMessageHandler, log]);

  return {
    // Основные методы
    setUrl,
    closeWindow,
    reset,

    // Состояние
    loading,
    error,
    isWindowOpen,
    popupState,

    // Вспомогательные методы
    sendMessageToPopup,
    isPopupReady,
    getPopupInfo,
    registerMessageHandler,
    unregisterMessageHandler,

    // Информация
    version: '2.0.0',
  };
};

// Хук-обертка для обратной совместимости с оригинальным API
export const useGamiPass = (options = {}) => {
  const { onError, onOpenAuth, onConfirm, onCanceled, onNotFound, windowFeatures = 'width=1024,height=1024' } = options;

  const { setUrl, closeWindow, loading, error, isWindowOpen } = useEnhancedGamiPass({
    onOpenAuth,
    onConfirm,
    onCanceled,
    onNotFound,
    onError,
    windowFeatures,
  });

  return {
    setUrl,
    closeWindow,
    loading,
    error,
    isWindowOpen,
  };
};
